import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DownloadIcon } from 'lucide-react'
import { useClubParticipationReport } from '@/api/hooks'
import { SelectShowNotice } from '@/components/current-show'
import { useCurrentShow } from '@/context/current-show'
import { ReportLayout } from '@/layouts/ReportLayout'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { triggerReportPrint } from '@/lib/print'
import { parseDateOnlyToLocal } from '@/lib/date'
import { sortClubRows } from './reports/club-participation-sort'

function parseIncludeUnknown(searchValue: string | null): boolean {
  if (!searchValue) return true
  return searchValue.toLowerCase() !== 'false'
}

export default function ClubParticipationReportPage() {
  const { currentShow } = useCurrentShow()
  const [searchParams] = useSearchParams()

  const showIdParam = searchParams.get('showId')
  const showNameParam = searchParams.get('showName')
  const showLocationParam = searchParams.get('showLocation')
  const showStartDateParam = searchParams.get('showStartDate')
  const showEndDateParam = searchParams.get('showEndDate')
  const includeUnknownParam = parseIncludeUnknown(searchParams.get('includeUnknown'))

  const showId = currentShow?.id ?? showIdParam ?? undefined
  const showName = currentShow?.name ?? showNameParam ?? 'Selected Show'
  const showLocation = currentShow?.location ?? showLocationParam ?? undefined
  const showStartDate = currentShow?.startDate ?? showStartDateParam ?? undefined
  const showEndDate = currentShow?.endDate ?? showEndDateParam ?? undefined

  const reportQuery = useClubParticipationReport(showId, {
    includeUnknown: includeUnknownParam,
    enabled: currentShow?.enableClubs !== false,
  })

  const numberFormatter = useMemo(() => new Intl.NumberFormat(), [])
  const percentFormatter = useMemo(
    () => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    []
  )

  const report = reportQuery.data
  const sortedRows = useMemo(() => {
    if (!report?.rows) return []
    return sortClubRows(report.rows, 'points', 'desc')
  }, [report])

  if (!showId) {
    return <SelectShowNotice description="Choose a show to print club participation totals." />
  }

  if (currentShow?.enableClubs === false || report?.enableClubs === false) {
    return (
      <SelectShowNotice
        title="Report unavailable"
        description="This report is unavailable for the selected show."
      />
    )
  }

  if (reportQuery.isError) {
    return (
      <p role="alert" className="text-destructive">
        {reportQuery.error?.message ?? 'Unable to load report.'}
      </p>
    )
  }

  if (reportQuery.isLoading || report?.showId !== showId) {
    return (
      <p role="status" className="text-muted-foreground">
        Loading report…
      </p>
    )
  }

  const header = (
    <div className="space-y-1">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">
            Club Participation
          </p>
          <h1 className="text-3xl font-semibold leading-tight">{showName}</h1>
          {showLocation ? <p className="text-sm text-muted-foreground">{showLocation}</p> : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="print-hidden"
          onClick={triggerReportPrint}
        >
          <DownloadIcon className="mr-2 h-4 w-4" aria-hidden="true" />
          Download PDF
        </Button>
      </div>
      {showStartDate && showEndDate
        ? (() => {
            const start = parseDateOnlyToLocal(showStartDate)
            const end = parseDateOnlyToLocal(showEndDate)
            if (!start || !end) return null
            const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
            return (
              <p className="text-sm text-muted-foreground">
                {formatter.format(start)} {' – '} {formatter.format(end)}
              </p>
            )
          })()
        : null}
    </div>
  )

  if (!report || sortedRows.length === 0) {
    return (
      <ReportLayout header={header}>
        <p className="text-muted-foreground text-base">No club participation has been recorded.</p>
      </ReportLayout>
    )
  }

  return (
    <ReportLayout header={header}>
      <section className="space-y-3">
        <h2 className="text-xl font-semibold">Show Totals</h2>
        <dl className="grid gap-3 sm:grid-cols-3 text-base">
          <div className="flex flex-col">
            <dt className="text-sm font-medium text-muted-foreground">Exhibitors</dt>
            <dd className="text-lg font-semibold">
              {numberFormatter.format(report.totals.entrants)}
            </dd>
          </div>
          <div className="flex flex-col">
            <dt className="text-sm font-medium text-muted-foreground">Entries</dt>
            <dd className="text-lg font-semibold">
              {numberFormatter.format(report.totals.entries)}
            </dd>
          </div>
          <div className="flex flex-col">
            <dt className="text-sm font-medium text-muted-foreground">Clubs Represented</dt>
            <dd className="text-lg font-semibold">{numberFormatter.format(report.rows.length)}</dd>
          </div>
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold report-layout__section-heading">
          Participation by Club
        </h2>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Club</TableHead>
                <TableHead className="text-right">Exhibitors</TableHead>
                <TableHead className="text-right">Entries</TableHead>
                <TableHead className="text-right">Total Points</TableHead>
                <TableHead className="text-right">Share of Exhibitors</TableHead>
                <TableHead className="text-right">Share of Entries</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map(row => (
                <TableRow key={`${row.clubId ?? 'none'}-${row.clubName}`}>
                  <TableCell className="font-medium">{row.clubName}</TableCell>
                  <TableCell className="text-right">
                    {numberFormatter.format(row.exhibitorCount)}
                  </TableCell>
                  <TableCell className="text-right">
                    {numberFormatter.format(row.entryCount)}
                  </TableCell>
                  <TableCell className="text-right">
                    {numberFormatter.format(row.totalPoints)}
                  </TableCell>
                  <TableCell className="text-right">
                    {percentFormatter.format(row.percentOfEntrants)}%
                  </TableCell>
                  <TableCell className="text-right">
                    {percentFormatter.format(row.percentOfEntries)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </ReportLayout>
  )
}
